import { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Pencil, Trash2, Search, X } from 'lucide-react'
import type { User, UserFilters } from '../types'
import { ConfirmDialog } from '@/shared/components/ui/molecules/ConfirmDialog'

const columnHelper = createColumnHelper<User>()

interface UsersTableProps {
  data: User[]
  total: number
  page: number
  limit: number
  filters: UserFilters
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onApplyFilters: (filters: Partial<UserFilters>) => void
  onClearFilters: () => void
  onDelete: (id: number) => void
  isLoading: boolean
  branchNames: Record<number, string>
}

export function UsersTable({
  data,
  total,
  page,
  limit,
  filters,
  onPageChange,
  onLimitChange,
  onApplyFilters,
  onClearFilters,
  onDelete,
  isLoading,
  branchNames,
}: UsersTableProps) {
  const [searchValue, setSearchValue] = useState(filters.value ?? '')
  const [deleteUser, setDeleteUser] = useState<User | null>(null)

  // Sync local state when applied filters change externally (e.g. clearFilters)
  useEffect(() => {
    setSearchValue(filters.value ?? '')
  }, [filters.value])

  const columns = [
    columnHelper.accessor('id', { header: 'ID', size: 60 }),
    columnHelper.accessor('name', {
      header: 'Nombre',
      cell: ({ row }) => `${row.original.name} ${row.original.last_name}`,
    }),
    columnHelper.accessor('rut', { header: 'RUT' }),
    columnHelper.accessor('email', { header: 'Email' }),
    columnHelper.accessor('id_branch_office', {
      header: 'Sucursal',
      cell: ({ getValue }) => branchNames[getValue()] ?? `Sucursal #${getValue()}`,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Acciones',
      size: 120,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Link
            to={`/users/${row.original.id}/edit`}
            className="text-primary-600 hover:text-primary-800 transition-colors"
            title="Editar"
          >
            <Pencil size={18} />
          </Link>
          <button
            onClick={() => setDeleteUser(row.original)}
            className="text-red-500 hover:text-red-700 transition-colors"
            title="Eliminar"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    }),
  ]

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: limit,
  }

  const pageCount = Math.ceil(total / limit)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      if (newPagination.pageSize !== limit) {
        onLimitChange(newPagination.pageSize)
      }
      if (newPagination.pageIndex + 1 !== page) {
        onPageChange(newPagination.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  const handleApply = () => {
    if (searchValue.trim()) {
      onApplyFilters({ param: 'rut', value: searchValue.trim() })
    } else {
      onClearFilters()
    }
  }

  const handleClear = () => {
    setSearchValue('')
    onClearFilters()
  }

  const handleConfirmDelete = () => {
    if (deleteUser) {
      onDelete(deleteUser.id)
      setDeleteUser(null)
    }
  }

  const hasActiveSearch = !!filters.value

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }

  return (
    <div>
      {/* Search bar */}
      <div className="p-4 border-b">
        <div className="flex gap-2 max-w-lg">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleApply() }}
              placeholder="Buscar por RUT..."
              className="input pl-9"
            />
          </div>
          <button onClick={handleApply} className="btn btn-primary">
            Buscar
          </button>
          {(searchValue || hasActiveSearch) && (
            <button onClick={handleClear} className="btn btn-secondary">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron vendedores
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {total} resultados - Página {page} de {pageCount || 1}
          </span>
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="input w-auto text-sm"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={!!deleteUser}
        title="Eliminar Vendedor"
        message={deleteUser ? `Estas seguro de eliminar a ${deleteUser.name} ${deleteUser.last_name}?` : ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteUser(null)}
      />
    </div>
  )
}
